import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { TenantSettingsValues } from './api';

interface TenantState {
  selectedTenantId: string | null;
  settings: TenantSettingsValues | null;
  /**
   * Product codes of the host-resolved tenant, or `null` until
   * `/tenants/resolve` answers. Read by the route guard in `_layout`'s
   * `beforeLoad`, which runs outside React and so cannot use the branding
   * context. `null` vs `[]` is load-bearing — see `lib/auth/product-scopes`.
   */
  enabledProducts: string[] | null;
}

const initialState: TenantState = {
  selectedTenantId: null,
  settings: null,
  enabledProducts: null,
};

export const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    setSelectedTenantId: (state, action: PayloadAction<string | null>) => {
      state.selectedTenantId = action.payload;
    },
    /**
     * Fill the tenant slot only if it is still empty.
     *
     * Used by the baked house-tenant fallback (`activeTenantId`) so it can seed
     * tenant scope on a host that doesn't resolve, without ever stomping an
     * explicit choice — a Super Admin's TenantSelector pick or the tenant
     * restored from localStorage. Doing the check in the reducer (rather than
     * reading `selectedTenantId` in the caller) keeps the caller from having to
     * subscribe to the value, which would make its effect re-fire on every
     * tenant switch and fight the selector.
     */
    setSelectedTenantIdIfUnset: (state, action: PayloadAction<string>) => {
      if (!state.selectedTenantId) state.selectedTenantId = action.payload;
    },
    clearSelectedTenant: (state) => {
      state.selectedTenantId = null;
    },
    setTenantSettings: (state, action: PayloadAction<TenantSettingsValues>) => {
      state.settings = action.payload;
    },
    clearTenantSettings: (state) => {
      state.settings = null;
    },
    setEnabledProducts: (state, action: PayloadAction<string[] | null>) => {
      state.enabledProducts = action.payload;
    },
  },
});

export const {
  setSelectedTenantId,
  setSelectedTenantIdIfUnset,
  clearSelectedTenant,
  setTenantSettings,
  clearTenantSettings,
  setEnabledProducts,
} = tenantSlice.actions;
export default tenantSlice.reducer;

